import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Button, Spinner } from '../../components/ui';
import ConfettiExplosion from '../../components/shared/ConfettiExplosion';
import ShareGraphicModal from '../../components/raffle/ShareGraphicModal';

/**
 * Create Success Page
 *
 * Shown after successful raffle creation.
 * No listing fee - raffles are active immediately.
 * Displays confirmation and links to view/share the raffle.
 */
export default function CreateSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [raffle, setRaffle] = useState(null);
  const [error, setError] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  const raffleId = searchParams.get('raffle_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!raffleId) {
      setError('Missing raffle ID');
      setLoading(false);
      return;
    }

    loadRaffle();
  }, [raffleId]);

  async function loadRaffle() {
    try {
      const raffleDoc = await getDoc(doc(db, 'raffles', raffleId));

      if (!raffleDoc.exists()) {
        setError('Raffle not found');
        setLoading(false);
        return;
      }

      setRaffle({ id: raffleDoc.id, ...raffleDoc.data() });
    } catch (err) {
      console.error('Error loading raffle:', err);
      setError('Failed to load raffle details');
    } finally {
      setLoading(false);
    }
  }

  // Stop confetti after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const shareUrl = raffle ? `${window.location.origin}/competition/${raffle.id}` : '';

  async function handleShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: raffle.title,
          text: `Check out my competition on Raflr: ${raffle.title}`,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Copy failed:', err);
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="page-container py-12 md:py-20">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-danger/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-3xl font-display text-cream mb-4">
              Something Went Wrong
            </h1>
            <p className="text-cream/60 mb-8">{error}</p>
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isPending = raffle?.status === 'pending';

  return (
    <div className="min-h-screen bg-midnight">
      {showConfetti && <ConfettiExplosion />}

      <div className="page-container py-12 md:py-20">
        <div className="max-w-xl mx-auto text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-success/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-display text-cream mb-4">
            {isPending ? 'Payment Processing...' : 'Your Competition is Live!'}
          </h1>

          {/* Subtitle */}
          <p className="text-cream/60 text-lg mb-8">
            {isPending
              ? 'Your competition will be activated shortly once payment is confirmed.'
              : 'Your competition has been published and is now accepting entries.'}
          </p>

          {/* Raffle Preview Card */}
          {raffle && (
            <div className="bg-midnight-light border border-midnight-lighter rounded-xl overflow-hidden mb-8 text-left">
              {/* Cover Image */}
              {raffle.photos?.[0] && (
                <div className="aspect-video">
                  <img
                    src={raffle.photos[0]}
                    alt={raffle.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Details */}
              <div className="p-5">
                <h2 className="text-xl font-semibold text-cream mb-2">{raffle.title}</h2>
                <div className="flex items-center gap-4 text-sm text-cream/60 mb-4">
                  <span>{raffle.ticketQuantity} entries</span>
                  <span>€{(raffle.ticketPrice / 100).toFixed(2)} each</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isPending
                      ? 'bg-warning/20 text-warning'
                      : 'bg-success/20 text-success'
                  }`}>
                    {isPending ? 'Pending Activation' : 'Active'}
                  </span>
                  <span className="text-cream/40 text-sm">{raffle.pickupArea}</span>
                </div>
              </div>
            </div>
          )}

          {/* Share Section */}
          {!isPending && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-5 mb-8">
              <h3 className="font-semibold text-cream mb-2">Share Your Competition</h3>
              <p className="text-cream/60 text-sm mb-4">
                Get the word out and get more entries!
              </p>
              <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
                {/* Instagram Story */}
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] hover:opacity-90 transition-opacity"
                >
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="text-xs font-medium text-white">Story</span>
                </button>

                {/* X (Twitter) */}
                <button
                  onClick={() => {
                    const text = encodeURIComponent(`Check out my competition on Raflr: ${raffle.title}`);
                    const url = encodeURIComponent(shareUrl);
                    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                  }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-midnight hover:bg-midnight-lighter transition-colors border border-cream/10"
                >
                  <svg className="w-6 h-6 text-cream" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="text-xs font-medium text-cream/70">X</span>
                </button>

                {/* Copy Link */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    alert('Link copied!');
                  }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-midnight hover:bg-midnight-lighter transition-colors border border-cream/10"
                >
                  <svg className="w-6 h-6 text-cream" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs font-medium text-cream/70">Copy</span>
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/competition/${raffleId}`}>
              <Button className="w-full sm:w-auto">
                View Your Competition
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full sm:w-auto">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Share Graphic Modal */}
      <ShareGraphicModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        raffle={raffle}
      />
    </div>
  );
}
